import { IImpersonationContext } from '@ViewModels';
import { css } from 'aphrodite';
import { FC } from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useDeleteTagCategoryMutation } from '../../../../../queries/TagCategory/useDeleteTagCategoryMutation';
import { invalidateGetTagCategoriesQuery } from '../../../../../queries/TagCategory/useGetTagCategoriesQuery';
import { Column, Row } from '../../../../components-v2/PageLayout/Layout';
import { Button, ButtonBase } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { WarningIcon, WarningIconType } from '../../../../components/svgs/icons/WarningIcon';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { useTagsDataContext } from '../TagsContexts/TagsDataContext';
import { styleSheet } from './styles';

interface DeleteCategoryModalProps {
	isOpen?: boolean;
	onClose?: () => void;
	impersonationContext: IImpersonationContext;
}

export const DeleteCategoryModal: FC<DeleteCategoryModalProps> = ({ isOpen, onClose, impersonationContext }) => {
	const errorMessages = useErrorMessages();
	const { selectedCategory, setSelectedCategory } = useTagsDataContext();

	const deleteCategory = useDeleteTagCategoryMutation({
		onSuccess: () => {
			invalidateGetTagCategoriesQuery();
			setSelectedCategory(null);
			toast.success('Category deleted successfully');
			onClose();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('An error occurred. Category was not deleted.');
			onClose();
		},
	});

	const handleDeleteCategory = () => {
		deleteCategory.mutate({
			categoryId: selectedCategory.id,
			deleteTags: false,
			impersonationContext,
		});
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<Column styles={[styleSheet.container]}>
				<Row styles={[styleSheet.headerRow]}>
					<ButtonBase onClick={onClose}>
						<XIcon height={14} width={14} />
					</ButtonBase>
				</Row>
				<Column styles={[styleSheet.formContainer]}>
					<Row styles={[styleSheet.warningRow]}>
						<WarningIcon type={WarningIconType.Blue} />
					</Row>
					<h3 className={css(styleSheet.warningText)}>Warning</h3>

					<p className={css(styleSheet.marginNone, styleSheet.paragraphSize)}>
						Are you sure you want to delete this category?
						<br />
						All tags will be removed from this category and it will <br /> be <strong>permanently deleted</strong>.
					</p>
					<Row styles={[styleSheet.buttonRow]}>
						<Button
							kind='primary'
							label='Delete this Category'
							styles={[styleSheet.destructive]}
							onClick={handleDeleteCategory}
						/>
						<Button kind='reverse' label='Cancel' onClick={onClose} />
					</Row>
				</Column>
			</Column>
		</Modal>
	);
};
